import { useMemo } from "react"
import {
  Button,
  DateRangePicker,
  MultiSelect,
} from "~/ui-rtk/components/ui/controls"
import { DATE_FORMAT, isValidDate, usePeriod } from "~/ui-rtk/hooks/period"
import useDate from "~/ui-rtk/hooks/date"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import useUrlColumnKeys from "~/ui-rtk/hooks/url-column-keys"
import { useURLSearchParams } from "~/ui-rtk/hooks/url"
import type { TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import type { TableVisualizationColumn } from "~/ui-rtk/constants/table-visualization-types"
import { exportTableToCSV } from "~/ui-rtk/utils/csv-export"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import useConnect, { type MatchType } from "./connect"
import ProductMediaPnLTable, {
  ALL_COLUMNS,
} from "./components/ProductMediaPnLTable"

const dayjs = useDate()

const URL_PREFIX = "product_media_pnl"
const MATCH_TYPE_URL_KEY = "match_type"

const MATCH_TYPE_OPTIONS: { id: MatchType; label: string }[] = [
  { id: "main", label: "Main Matches" },
  { id: "all", label: "All Matches" },
]

export default function ProductMediaPnLPage() {
  const { params, setParams } = useURLSearchParams()
  const { period: dateRange, setPeriod } = usePeriod()

  const matchType: MatchType =
    params[MATCH_TYPE_URL_KEY] === "all" ? "all" : "main"

  // Column selection
  const defaultColumnKeys = useMemo(
    () =>
      ALL_COLUMNS.filter(
        (column: TableVisualizationColumn & { disabled?: boolean }) =>
          !column.disabled,
      ).map((column: TableVisualizationColumn) => column.key),
    [],
  )

  const { columnKeys, setColumnKeys } = useUrlColumnKeys(
    URL_PREFIX,
    defaultColumnKeys,
  )

  const columnOptions = useMemo(
    () =>
      ALL_COLUMNS.map(
        (column: TableVisualizationColumn) =>
          ({
            value: column.key,
            label: column.header,
          }) as TMultiSelectOption,
      ),
    [],
  )

  const columnConfigFiltered = useMemo(
    () =>
      ALL_COLUMNS.filter(({ key }: { key: string }) =>
        (columnKeys as string[])?.includes(key),
      ),
    [columnKeys],
  )

  const { isLoading, error, tableData } = useConnect(dateRange, matchType)

  // Sort client-side (default: highest total spent first) — server-side sort is
  // impossible across the two merged queries.
  const sortedData = useMemo(
    () => [...tableData].sort((a, b) => b.totalSpent - a.totalSpent),
    [tableData],
  )

  const changePeriod = ({ start, end }: { start: Date; end: Date }) => {
    void setPeriod(
      dayjs(start).format(DATE_FORMAT),
      dayjs(end).format(DATE_FORMAT),
    )
  }

  const handleExportCSV = () => {
    exportTableToCSV(
      sortedData as unknown as Record<string, unknown>[],
      columnConfigFiltered,
      { filename: `product-media-pnl-${dayjs().format("YYYY-MM-DD")}.csv` },
    )
  }

  const dateRangeStart = useMemo(
    () =>
      dateRange?.[0] && isValidDate(dateRange[0])
        ? dayjs(dateRange[0]).toDate()
        : new Date(),
    [dateRange?.[0]],
  )

  const dateRangeEnd = useMemo(
    () =>
      dateRange?.[1] && isValidDate(dateRange[1])
        ? dayjs(dateRange[1]).toDate()
        : new Date(),
    [dateRange?.[1]],
  )

  return (
    <div className="px-4">
      <div className="flex items-center justify-between">
        <Breadcrumbs
          items={[
            {
              name: "Web Merchandising",
              slug: brandRoutes.WEB_MERCHANDISING.ROOT,
            },
            {
              name: "Product Media P&L",
              slug: brandRoutes.WEB_MERCHANDISING.PRODUCT_MEDIA_PNL,
            },
          ]}
        />
      </div>

      {/* Exposure attribution note — ad metrics are non-additive across products */}
      <div className="mt-system-md rounded-system-md border border-system-blue-600 bg-system-blue-800 px-4 py-3 text-sm text-system-gray-300">
        Ad metrics (Active Ads, Total Spent, Modeled Revenue, Platform Revenue,
        Brand Value, Modeled Brand Value) are{" "}
        <span className="font-semibold text-white">exposure attribution</span>:
        each ad is counted against every product it was matched to, so these
        values are double-counted across products and{" "}
        <span className="font-semibold text-white">
          do not sum to an account total
        </span>
        . Sales metrics come from product page analytics.
      </div>

      <div className="flex justify-between items-center gap-system-sm mt-system-lg mb-system-lg">
        <div className="join">
          {MATCH_TYPE_OPTIONS.map(({ id, label }) => (
            <Button
              variant={{ variant: "solid", color: "blue" }}
              key={id}
              title={label}
              className={cn(
                "join-item border border-system-blue-600 h-system-2xl px-system-lg py-system-sm rounded-system-md",
                { "bg-system-blue-800": matchType !== id },
                { "bg-system-blue-600": matchType === id },
              )}
              onClick={() => setParams({ [MATCH_TYPE_URL_KEY]: id })}
            >
              <span
                className={cn("font-[Lato]", {
                  "text-base text-system-gray-300 font-normal":
                    matchType !== id,
                  "text-white font-semibold": matchType === id,
                })}
              >
                {label}
              </span>
            </Button>
          ))}
        </div>

        {dateRange?.[0] &&
          dateRange?.[1] &&
          isValidDate(dateRange[0]) &&
          isValidDate(dateRange[1]) && (
            <DateRangePicker
              start={dateRangeStart}
              end={dateRangeEnd}
              comparisonEnabled={false}
              onChange={changePeriod}
            />
          )}
      </div>

      <div className="flex justify-end items-center gap-system-sm mt-3 mb-3">
        <MultiSelect
          label="Columns"
          options={columnOptions}
          value={columnOptions.filter((option: TMultiSelectOption) =>
            (columnKeys as string[]).includes(option.value),
          )}
          onChange={(options: TMultiSelectOption[]) =>
            setColumnKeys(options.map(({ value }) => value))
          }
        />
        <Button
          variant={{ variant: "solid", color: "blue" }}
          className="border border-system-blue-600 h-system-2xl px-system-lg py-system-sm rounded-system-md bg-system-blue-800"
          onClick={handleExportCSV}
          disabled={sortedData.length === 0}
        >
          <span className="font-[Lato] text-sm">Export CSV</span>
        </Button>
      </div>

      {error ? (
        <div className="mt-6 p-4 bg-error/10 border border-error rounded-lg text-error text-sm">
          Failed to load data: {error.message || "An unexpected error occurred"}
        </div>
      ) : (
        <ProductMediaPnLTable
          data={sortedData}
          isLoading={isLoading}
          columnConfig={columnConfigFiltered}
        />
      )}
    </div>
  )
}
